import React, { useEffect, useRef, useState } from "react";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { classNames } from "src/helpers/classNames";
import { useParams } from "react-router-dom";
import HeaderMenuGroup from "./headerMenu/HeaderMenuGroup";
import HeaderMenuItem from "./headerMenu/HeaderMenuItem";

const HeaderMenus = ({ menus, setOverflowedHeaderMenus, mordernDesign }) => {
  const { group: group_id, page, form, document } = useUrlQueryParams();
  const { content_value } = useParams();

  const [maxVisible, setMaxVisible] = useState(5);
  const [isHoveringMore, setIsHoveringMore] = useState(false);

  const visibleMenus = menus.slice(0, maxVisible);
  const overflowMenus = menus.slice(maxVisible);

  const hoverTimeout = useRef(null);

  const onEnter = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setIsHoveringMore(true);
  };

  const onLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setIsHoveringMore(false);
    }, 150);
  };

  const menu_id = page || document || form || content_value;

  const calculateMaxVisible = () => {
    const width = window.innerWidth;
    if (width < 700) return 2;
    if (width < 800) return 2;
    if (width < 1400) return 4;
    if (width < 1500) return 5;
    if (width > 1500) return 6;
    return 7;
  };

  useEffect(() => {
    const handleResize = () => {
      setMaxVisible(calculateMaxVisible());
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1280) {
      setOverflowedHeaderMenus(menus);
    } else {
      setOverflowedHeaderMenus([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus, window.innerWidth]);

  if (!menus || menus.length === 0) return null;

  const isMenuItemActive = (item) => {
    if (item?.isGroup) {
      return Boolean(item?.submenu?.find((subItem) => subItem?.slug === menu_id));
    }

    if (item.sub) {
      return (item._id === menu_id || item.slug === menu_id) && item.groupId === group_id;
    }

    return item._id === menu_id || item.slug === menu_id;
  };

  const moreButtonClasses = classNames(
    "flex min-h-10 items-center gap-1 rounded px-3 py-1 font-medium transition-all",
    mordernDesign ? "hover:bg-leftNavActiveColor hover:text-leftNavActiveTextColor" : "hover:bg-headerMenuActiveBgColor hover:text-headerMenuActiveTextColor",
    overflowMenus?.some(isMenuItemActive) ? (mordernDesign ? "bg-leftNavActiveColor text-leftNavActiveTextColor" : "bg-headerMenuActiveBgColor text-headerMenuActiveTextColor") : mordernDesign ? "text-leftNavTextColor" : "text-headerTextColor",
  );

  return (
    <div className="animate-fade-in hidden items-center space-x-2 px-3 text-sm xl:flex">
      {visibleMenus.map((item) => {
        if (item.isGroup) {
          return (
            <HeaderMenuGroup
              key={item._id}
              data={item}
              group_id={group_id}
              menu_id={menu_id}
              searchTerm=""
              dropdownPosition="right"
              mordernDesign={mordernDesign}
            />
          );
        }

        return (
          <HeaderMenuItem
            key={item._id}
            data={item}
            group_id={group_id}
            image={item.image}
            menu_id={menu_id}
            mordernDesign={mordernDesign}
          />
        );
      })}

      {overflowMenus.length > 0 && window.innerWidth > 700 && (
        <Menu
          as="div"
          className="relative">
          <div
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            className="relative">
            <Menu.Button className={moreButtonClasses}>
              More
              {isHoveringMore ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
            </Menu.Button>

            {isHoveringMore && (
              <Menu.Items
                static
                className={classNames("absolute right-0 top-11 z-[50] mt-2 w-56 rounded-md shadow-lg ring-1 ring-black ring-opacity-5", mordernDesign ? "bg-leftNavColor" : "bg-headerColor")}>
                <div className="space-y-1 p-1">
                  {overflowMenus.map((item) => (
                    <Menu.Item
                      key={item._id}
                      className="rounded-md p-1">
                      {item.isGroup ? (
                        <HeaderMenuGroup
                          data={item}
                          group_id={group_id}
                          menu_id={menu_id}
                          dropdownPosition="bottom-left"
                          mordernDesign={mordernDesign}
                        />
                      ) : (
                        <HeaderMenuItem
                          data={item}
                          group_id={group_id}
                          menu_id={menu_id}
                          mordernDesign={mordernDesign}
                        />
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            )}
          </div>
        </Menu>
      )}
    </div>
  );
};

export default HeaderMenus;
